import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Tablet, Plus, Power, Clock, AlertCircle, Copy, CheckCircle2, Trash2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { config } from '../../lib/config';
import { useAuthStore, selectIsAdmin } from '../../stores/auth.store';
const TIME_API_URL = config.api.time;
/**
 * Get authorization headers with JWT token from auth store
 */
function getAuthHeaders() {
    const { tokens, user } = useAuthStore.getState();
    const token = tokens?.idToken;
    const tenantId = user?.tenantId || 'tenant-001';
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Tenant-Id': tenantId,
    };
}
function CreateDeviceModal({ isOpen, onClose, onSuccess }) {
    const [name, setName] = useState('');
    const [location, setLocation] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pairingCode, setPairingCode] = useState(null);
    const [expiresAt, setExpiresAt] = useState(null);
    const [copied, setCopied] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState('');
    // Countdown timer effect
    useEffect(() => {
        if (!expiresAt)
            return;
        const updateTimer = () => {
            const now = new Date().getTime();
            const expires = new Date(expiresAt).getTime();
            const diff = expires - now;
            if (diff <= 0) {
                setTimeRemaining('Expired');
                return;
            }
            const minutes = Math.floor(diff / 60000);
            const seconds = Math.floor((diff % 60000) / 1000);
            setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        };
        updateTimer(); // Initial call
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [expiresAt]);
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        try {
            const response = await fetch(`${TIME_API_URL}/kiosk/devices`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    name: name.trim(),
                    location: location.trim() || undefined,
                }),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to create device');
            }
            const data = await response.json();
            setPairingCode(data.pairingCode);
            setExpiresAt(data.pairingExpiresAt);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create device');
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleCopyCode = () => {
        if (pairingCode) {
            void navigator.clipboard.writeText(pairingCode);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };
    const handleClose = () => {
        setName('');
        setLocation('');
        setPairingCode(null);
        setExpiresAt(null);
        setError(null);
        setCopied(false);
        onClose();
        if (pairingCode) {
            onSuccess();
        }
    };
    if (!isOpen)
        return null;
    // Build full pairing URL - use app.lalohr.com in production, otherwise current origin
    const isProduction = window.location.hostname === 'app.lalohr.com' || window.location.hostname === 'lalohr.com';
    const appDomain = isProduction ? 'https://app.lalohr.com' : window.location.origin;
    const pairingUrl = `${appDomain}/kiosk/pair`;
    return (_jsx("div", { className: "fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4", children: _jsxs(Card, { className: "w-full max-w-md", children: [_jsx(CardHeader, { children: _jsxs(CardTitle, { className: "flex items-center gap-2", children: [_jsx(Tablet, { className: "w-5 h-5" }), pairingCode ? 'Pairing Code' : 'Create Kiosk Device'] }) }), _jsxs(CardContent, { children: [error && (_jsxs(Alert, { variant: "destructive", className: "mb-4", children: [_jsx(AlertCircle, { className: "h-4 w-4" }), _jsx(AlertDescription, { children: error })] })), !pairingCode ? (_jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium mb-1", children: ["Device Name ", _jsx("span", { className: "text-red-500", children: "*" })] }), _jsx("input", { type: "text", value: name, onChange: (e) => setName(e.target.value), placeholder: "e.g., Front Desk, Warehouse Floor", className: "w-full px-3 py-2 border rounded-md", required: true, maxLength: 100 })] }), _jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium mb-1", children: ["Location ", _jsx("span", { className: "text-gray-500 text-xs", children: "(Optional)" })] }), _jsx("input", { type: "text", value: location, onChange: (e) => setLocation(e.target.value), placeholder: "e.g., Main Office Building A", className: "w-full px-3 py-2 border rounded-md", maxLength: 200 })] }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { type: "button", variant: "outline", onClick: handleClose, className: "flex-1", disabled: isLoading, children: "Cancel" }), _jsx(Button, { type: "submit", className: "flex-1", disabled: isLoading, children: isLoading ? 'Creating...' : 'Create Device' })] })] })) : (_jsxs("div", { className: "space-y-4", children: [_jsxs(Alert, { children: [_jsx(CheckCircle2, { className: "h-4 w-4" }), _jsx(AlertDescription, { children: "Device created successfully! Use this code to pair the kiosk device." })] }), _jsxs("div", { className: "bg-gray-50 border-2 border-dashed rounded-lg p-6 text-center", children: [_jsx("p", { className: "text-sm text-gray-600 mb-2", children: "Pairing Code" }), _jsx("div", { className: "text-4xl font-mono font-bold tracking-wider mb-3", children: pairingCode }), _jsx(Button, { onClick: handleCopyCode, variant: "outline", size: "sm", className: "gap-2", children: copied ? (_jsxs(_Fragment, { children: [_jsx(CheckCircle2, { className: "w-4 h-4" }), "Copied!"] })) : (_jsxs(_Fragment, { children: [_jsx(Copy, { className: "w-4 h-4" }), "Copy Code"] })) })] }), _jsx("div", { className: "bg-yellow-50 border border-yellow-200 rounded-lg p-4", children: _jsxs("div", { className: "flex items-start gap-2", children: [_jsx(Clock, { className: "w-5 h-5 text-yellow-600 mt-0.5" }), _jsxs("div", { className: "flex-1", children: [_jsxs("p", { className: "text-sm font-medium text-yellow-900", children: ["Code expires in: ", _jsx("span", { className: "font-mono", children: timeRemaining })] }), _jsx("p", { className: "text-xs text-yellow-700 mt-2", children: "Navigate to this URL on the kiosk device to complete pairing:" }), _jsx("code", { className: "block bg-yellow-100 px-2 py-1 rounded text-xs mt-1 break-all", children: pairingUrl })] })] }) }), _jsx(Button, { onClick: handleClose, className: "w-full", children: "Done" })] }))] })] }) }));
}
export default function TimeClockDevicesPage() {
    const isAdmin = useAuthStore(selectIsAdmin);
    const [devices, setDevices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const loadDevices = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`${TIME_API_URL}/kiosk/devices`, {
                headers: getAuthHeaders(),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to load devices');
            }
            const data = await response.json();
            setDevices(data.devices || []);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load devices');
        }
        finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        if (isAdmin) {
            void loadDevices();
        }
    }, [isAdmin]);
    const handleDisableDevice = async (deviceId) => {
        if (!confirm('Are you sure you want to disable this device? It will no longer be able to clock employees in/out.')) {
            return;
        }
        try {
            const response = await fetch(`${TIME_API_URL}/kiosk/devices/${deviceId}/disable`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to disable device');
            }
            await loadDevices();
        }
        catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to disable device');
        }
    };
    const handleDeleteDevice = async (deviceId, deviceName) => {
        if (!confirm(`Are you sure you want to permanently delete "${deviceName}"? This action cannot be undone.`)) {
            return;
        }
        try {
            const response = await fetch(`${TIME_API_URL}/kiosk/devices/${deviceId}`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to delete device');
            }
            await loadDevices();
        }
        catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to delete device');
        }
    };
    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return _jsx(Badge, { className: "bg-green-600 text-white", children: "Active" });
            case 'pending':
                return _jsx(Badge, { className: "bg-yellow-600 text-white", children: "Pending" });
            case 'disabled':
                return _jsx(Badge, { variant: "secondary", children: "Disabled" });
            default:
                return _jsx(Badge, { variant: "secondary", children: status });
        }
    };
    const formatDate = (dateStr) => {
        if (!dateStr)
            return 'Never';
        return new Date(dateStr).toLocaleString();
    };
    if (!isAdmin) {
        return (_jsx("div", { className: "p-6 max-w-4xl mx-auto", children: _jsxs(Alert, { variant: "destructive", children: [_jsx(AlertCircle, { className: "h-4 w-4" }), _jsx(AlertDescription, { children: "Only administrators can manage kiosk devices." })] }) }));
    }
    return (_jsxs("div", { className: "p-6 max-w-6xl mx-auto space-y-6", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsxs("div", { className: "flex items-center gap-3 mb-2", children: [_jsx(Tablet, { className: "w-8 h-8 text-blue-600" }), _jsx("h1", { className: "text-3xl font-bold", children: "Time Clock Devices" })] }), _jsx("p", { className: "text-muted-foreground", children: "Manage kiosk devices for employee time tracking" })] }), _jsxs(Button, { onClick: () => setIsCreateModalOpen(true), className: "gap-2", children: [_jsx(Plus, { className: "w-4 h-4" }), "Add Device"] })] }), error && (_jsxs(Alert, { variant: "destructive", children: [_jsx(AlertCircle, { className: "h-4 w-4" }), _jsx(AlertDescription, { children: error })] })), isLoading ? (_jsx("div", { className: "flex items-center justify-center py-12", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2" }), _jsx("p", { className: "text-sm text-gray-600", children: "Loading devices..." })] }) })) : devices.length === 0 ? (_jsx(Card, { children: _jsxs(CardContent, { className: "py-12 text-center", children: [_jsx(Tablet, { className: "w-12 h-12 text-gray-400 mx-auto mb-4" }), _jsx("h3", { className: "text-lg font-semibold mb-2", children: "No devices yet" }), _jsx("p", { className: "text-sm text-gray-600 mb-4", children: "Create your first kiosk device to enable time clock functionality" }), _jsxs(Button, { onClick: () => setIsCreateModalOpen(true), className: "gap-2", children: [_jsx(Plus, { className: "w-4 h-4" }), "Add Your First Device"] })] }) })) : (_jsx("div", { className: "grid gap-4 md:grid-cols-2 lg:grid-cols-3", children: devices.map((device) => (_jsxs(Card, { children: [_jsx(CardHeader, { children: _jsxs("div", { className: "flex items-start justify-between", children: [_jsxs("div", { className: "flex-1", children: [_jsx(CardTitle, { className: "text-lg mb-1", children: device.name }), device.location && (_jsx("p", { className: "text-sm text-gray-600", children: device.location }))] }), getStatusBadge(device.status)] }) }), _jsxs(CardContent, { className: "space-y-3", children: [_jsxs("div", { className: "text-sm space-y-1", children: [_jsxs("div", { className: "flex justify-between", children: [_jsx("span", { className: "text-gray-600", children: "Created:" }), _jsx("span", { className: "font-medium", children: formatDate(device.createdAt) })] }), device.pairedAt && (_jsxs("div", { className: "flex justify-between", children: [_jsx("span", { className: "text-gray-600", children: "Paired:" }), _jsx("span", { className: "font-medium", children: formatDate(device.pairedAt) })] })), device.lastUsedAt && (_jsxs("div", { className: "flex justify-between", children: [_jsx("span", { className: "text-gray-600", children: "Last Used:" }), _jsx("span", { className: "font-medium", children: formatDate(device.lastUsedAt) })] }))] }), device.status === 'pending' && device.pairingCode && (_jsxs("div", { className: "bg-yellow-50 border border-yellow-200 rounded p-3", children: [_jsx("p", { className: "text-xs text-yellow-800 mb-1", children: "Pairing Code:" }), _jsx("p", { className: "font-mono text-lg font-bold text-yellow-900", children: device.pairingCode }), _jsxs("p", { className: "text-xs text-yellow-700 mt-1", children: ["Expires: ", formatDate(device.pairingExpiresAt)] })] })), _jsxs("div", { className: "space-y-2", children: [device.status === 'active' && (_jsxs(Button, { onClick: () => handleDisableDevice(device.deviceId), variant: "destructive", size: "sm", className: "w-full gap-2", children: [_jsx(Power, { className: "w-4 h-4" }), "Disable Device"] })), _jsxs(Button, { onClick: () => handleDeleteDevice(device.deviceId, device.name), variant: "destructive", size: "sm", className: "w-full gap-2", children: [_jsx(Trash2, { className: "w-4 h-4" }), "Delete Device"] })] })] })] }, device.deviceId))) })), _jsx(CreateDeviceModal, { isOpen: isCreateModalOpen, onClose: () => setIsCreateModalOpen(false), onSuccess: loadDevices })] }));
}
